import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Mail, MailCheck } from "lucide-react";

import { Alert, AlertDescription } from "@/core/components/ui/alert";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/core/components/ui/card";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useForgotPassword } from "@/features/auth/api/use-forgot-password";
import {
  createForgotPasswordSchema,
  type ForgotPasswordFormValues,
} from "@/features/auth/schemas/forgot-password.schema";

/**
 * Formulaire « mot de passe oublié » (guide API v1.3) : email envoyé au
 * endpoint PUBLIC `Passwords/forgot`. Le backend répond toujours 202
 * (anti-énumération) — après succès, l'UI affiche le même message générique,
 * sans jamais indiquer si le compte existe.
 */
export function ForgotPasswordForm() {
  const { t } = useTypedTranslation();
  const { i18n } = useTranslation();
  const forgotPassword = useForgotPassword();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue (schéma zod traduit reconstruit au changement de langue).
  const schema = useMemo(() => createForgotPasswordSchema(), [i18n.language]);

  const form = useForm({
    defaultValues: { email: "" } as ForgotPasswordFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setSubmitError(null);
      try {
        await forgotPassword.mutateAsync(parsed.data);
        setEmailSent(true);
      } catch {
        // Échec réseau / serveur uniquement (l'API répond 202 sinon) :
        // message générique, sans lien avec l'existence du compte.
        setSubmitError(t("error"));
      }
    },
  });

  const fieldError =
    (name: keyof ForgotPasswordFormValues) =>
    ({ value }: { value: unknown }) => {
      const result = schema.shape[name].safeParse(value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  if (emailSent) {
    return (
      <div className="flex w-full max-w-md flex-col gap-6">
        <Card className="w-full shadow-lg">
          <CardHeader className="items-center gap-3">
            <CardDescription className="flex items-center gap-1.5 text-muted-foreground">
              <img
                src="/flash_pay_logo.png"
                alt=""
                aria-hidden="true"
                className="size-4"
              />
              <MailCheck className="size-3.5" />
              {t("auth.forgotPassword.title")}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Alert className="border-success bg-success-muted">
              <AlertDescription>
                {t("auth.forgotPassword.success")}
              </AlertDescription>
            </Alert>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">{t("auth.forgotPassword.backToLogin")}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-md flex-col gap-6">
      <Card className="w-full shadow-lg">
        <CardHeader className="items-center gap-3">
          <CardDescription className="flex items-center gap-1.5 text-muted-foreground">
            <img
              src="/flash_pay_logo.png"
              alt=""
              aria-hidden="true"
              className="size-4"
            />
            <Mail className="size-3.5" />
            {t("auth.forgotPassword.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            {submitError ? (
              <Alert variant="destructive">
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            ) : null}

            <form.Field name="email" validators={{ onChange: fieldError("email") }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="email">{t("auth.forgotPassword.email")}</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder={t("auth.forgotPassword.emailPlaceholder")}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? <ButtonSpinner /> : null}
                  {t("auth.forgotPassword.submit")}
                </Button>
              )}
            </form.Subscribe>

            <Button asChild variant="link" className="w-full">
              <Link to="/login">{t("auth.forgotPassword.backToLogin")}</Link>
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
